import { useContext } from "react";
import { useQueries, useQuery, useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import { UserContext } from "../contexts";
import { readDashboardEntry } from "./dashboardCache";

/**
 * Dashboard v2 reads — one aggregate call per card group, nothing paged into
 * the browser. Contract: /api/dashboard/{me,actions,my-day,store,executive,reports}.
 *
 * Every key carries the ACTIVE STORE (`storeKey`): the store switcher changes
 * the X-Active-Store header and a key without it would hand the new store the
 * old store's cached numbers. `metadata.silent` keeps failures out of the global
 * toast — each card shows its own error panel with Retry. `retry: false`: a
 * dashboard that hammers a failing endpoint helps nobody. `enabled` is false
 * when the plan lacks analytics_core, so a locked store fires no 403s at all.
 *
 * Freshness (2026-10-22): every query is seeded from the user's own persisted
 * copy (dashboardCache — `initialData` + `initialDataUpdatedAt`), so a revisit
 * paints at once, and is then revalidated in the background: staleTime 0,
 * refetch on focus / reconnect, an interval fallback, plus the /version poll
 * (useDashboardFreshness). Writes back to storage happen in one place —
 * useDashboardPersistence, a query-cache subscriber.
 */

export const SILENT = { metadata: { silent: true } };

/**
 * The active store id as a key segment. Never falls back to the home store
 * (store_id_m): an unscoped session must not read/write that store's cache.
 */
export const useStoreKey = () => {
  const { activeStoreId } = useContext(UserContext) || {};
  return activeStoreId ?? "unscoped";
};

/** Whose persisted copy to read: the signed-in user's id (none = no persistence). */
export const useCacheOwner = () => {
  const { user } = useContext(UserContext) || {};
  return user?.id ?? null;
};

export const dashKey = (name, storeKey, ...rest) => ["dashboard", name, storeKey ?? "home", ...rest];

// Views whose data is persisted and refreshed when /version moves.
export const PERSISTED_VIEWS = ["me", "actions", "my-day", "store", "executive", "reports", "settings"];

export const ACTIONS_INTERVAL = 60 * 1000;
export const SNAPSHOT_INTERVAL = 5 * 60 * 1000;

// ── ETags ────────────────────────────────────────────────────────────────────
// /store, /executive, /reports and /version answer with an ETag. We send it back
// as If-None-Match; a 304 (empty body) means "what you hold is current" and the
// query keeps the data it already has. The tag is remembered per user + key (and
// persisted next to the data). Cross-origin the browser hides the ETag header
// (the API's CORS exposed_headers is empty), so the tag is taken from the header
// when readable, else from the body (`etag` on snapshot payloads), else from
// `etagOf(data)` (/version's is derivable). /me, /actions, /my-day have none.
const etags = new Map();
const tagKey = (userId, key) => `${userId}|${JSON.stringify(key)}`;
export const etagFor = (userId, key) => {
  const k = tagKey(userId, key);
  if (etags.has(k)) return etags.get(k);
  return readDashboardEntry(userId, key)?.etag || null;
};
export const setEtag = (userId, key, tag) => {
  if (tag) etags.set(tagKey(userId, key), tag);
  else etags.set(tagKey(userId, key), null);
};
export const resetEtags = () => etags.clear();

export class NotModifiedWithoutDataError extends Error {}

const okOr304 = (s) => (s >= 200 && s < 300) || s === 304;

/**
 * GET with If-None-Match when we hold both a tag and data for this key.
 * Returns the axios response; a 304 is turned into `{ status: 304, data: current }`.
 */
export const conditionalGet = async (url, params, { userId, key, getCurrent, signal, etagOf } = {}) => {
  const current = getCurrent ? getCurrent() : undefined;
  const tag = key && userId != null && current !== undefined ? etagFor(userId, key) : null;
  const send = (withTag) =>
    axios.get(url, {
      params,
      signal,
      validateStatus: okOr304,
      ...(withTag ? { headers: { "If-None-Match": withTag } } : null),
      ...SILENT,
    });
  let res = await send(tag);
  // The global interceptor can resolve a failed request as undefined — that is a failure.
  if (res === undefined || res === null) throw new Error("The request did not complete.");
  if (res.status === 304) {
    if (current !== undefined) return { status: 304, data: current, headers: res.headers };
    // A 304 with nothing to keep (should not happen): ask again unconditionally.
    res = await send(null);
    if (!res || res.status === 304) throw new NotModifiedWithoutDataError("Nothing cached to keep.");
  }
  if (key && userId != null && res.status === 200) {
    const d = res.data;
    const tag = res.headers?.etag || res.headers?.ETag || (d && typeof d.etag === "string" ? d.etag : null) || (etagOf ? etagOf(d) : null);
    setEtag(userId, key, tag || null);
  }
  return res;
};

/** `initialData` / `initialDataUpdatedAt` from the persisted copy (read once per query creation). */
export const seedFrom = (userId, key) => {
  let entry;
  let read = false;
  const get = () => {
    if (!read) {
      read = true;
      entry = readDashboardEntry(userId, key);
    }
    return entry;
  };
  return {
    initialData: () => get()?.data,
    initialDataUpdatedAt: () => get()?.updatedAt,
  };
};

// Shared freshness options: always revalidate on display, focus and reconnect.
const live = (interval) => ({
  staleTime: 0,
  refetchOnWindowFocus: true,
  refetchOnReconnect: true,
  refetchInterval: interval || false,
  refetchIntervalInBackground: false,
});

const useListQuery = ({ name, storeKey, rest = [], path, params, enabled, interval, keepPrevious = false }) => {
  const qc = useQueryClient();
  const userId = useCacheOwner();
  const key = dashKey(name, storeKey, ...rest);
  return useQuery({
    queryKey: key,
    queryFn: async ({ signal }) => (await conditionalGet(`/api/dashboard/${path}`, params, { userId, key, signal, getCurrent: () => qc.getQueryData(key) })).data,
    ...seedFrom(userId, key),
    ...live(interval),
    keepPreviousData: keepPrevious,
    enabled,
    retry: false,
  });
};

// `rep_id` is only sent when someone chose a rep; omitted = the API's default
// (the viewer if they are a rep of the store, else the whole store).
const repParams = (repId) => (repId ? { rep_id: repId } : undefined);

export const useDashboardMe = (storeKey, enabled = true) => useListQuery({ name: "me", storeKey, path: "me", enabled });

export const useDashboardActions = (repId, storeKey, enabled = true) =>
  useListQuery({ name: "actions", storeKey, rest: [repId ?? "default"], path: "actions", params: repParams(repId), enabled, interval: ACTIONS_INTERVAL, keepPrevious: true });

export const useDashboardMyDay = (repId, storeKey, enabled = true) =>
  useListQuery({ name: "my-day", storeKey, rest: [repId ?? "default"], path: "my-day", params: repParams(repId), enabled, interval: ACTIONS_INTERVAL, keepPrevious: true });


// ── Snapshot endpoints: /store, /executive, /reports (round 2) ────────────────
//
// These are computed by a background runner on the API. Asked with `part=`
// (store / executive) or `async=1` (reports), the API answers at once: 200 with
// the stored snapshot (`computed_at`, `stale`), or 202 `{status:"computing",
// retry_after}` when nothing is stored yet. The query function polls the 202
// itself, so React Query sees ONE long fetch: the card shows its skeleton (or,
// while switching period, the previous numbers — keepPreviousData; or the
// persisted copy) and never an error just because a cold period is still being computed.

export const POLL_LIMIT_MS = 3 * 60 * 1000;
const sleep = (ms, signal) =>
  new Promise((resolve, reject) => {
    const t = setTimeout(resolve, ms);
    signal?.addEventListener?.("abort", () => {
      clearTimeout(t);
      reject(new Error("aborted"));
    });
  });

export class StillComputingError extends Error {
  constructor() {
    super("The dashboard is still being computed.");
    this.name = "StillComputingError";
    this.computing = true;
  }
}

const isComputing = (res) => res?.status === 202 || res?.data?.status === "computing";

/** GET a snapshot endpoint, polling while the API says 202 computing. A 304 keeps `getCurrent()`. */
export const fetchSnapshot = async (path, params, { signal, limitMs = POLL_LIMIT_MS, now = () => Date.now(), userId, key, getCurrent } = {}) => {
  const started = now();
  for (;;) {
    const res = await conditionalGet(`/api/dashboard/${path}`, params, { signal, userId, key, getCurrent });
    if (!isComputing(res)) return res.data;
    if (now() - started >= limitMs) throw new StillComputingError();
    const wait = Math.min(30, Math.max(1, Number(res.data?.retry_after) || 3)) * 1000;
    await sleep(wait, signal);
  }
};

// One automatic retry on a network error or 5xx before the error panel; never
// on a 4xx (403 / 422 are answers, not hiccups) or a still-computing timeout.
export const retryOnce = (count, error) => {
  if (count >= 1 || error?.computing) return false;
  const status = error?.response?.status;
  return !status || status >= 500;
};

// A `stale:true` snapshot means a recompute is already queued on the API (data
// changed, or past its TTL): look again in 30 s instead of the 5-min fallback.
export const STALE_RECHECK = 30 * 1000;
export const snapshotInterval = (data) => (data?.stale === true ? STALE_RECHECK : SNAPSHOT_INTERVAL);

const snapshotQuery = ({ key, path, params, enabled, userId, qc }) => ({
  queryKey: key,
  queryFn: ({ signal }) => fetchSnapshot(path, params, { signal, userId, key, getCurrent: () => qc.getQueryData(key) }),
  ...seedFrom(userId, key),
  ...live(snapshotInterval),
  // Switching period keeps the old numbers on screen instead of flashing skeletons.
  keepPreviousData: true,
  enabled,
  retry: retryOnce,
  retryDelay: 1500,
});

export const STORE_PARTS = ["kpis", "trend", "floor", "pipeline", "team", "clients"];
export const EXECUTIVE_PARTS = ["margin", "products", "portfolio", "cltv", "wa"];

// Parts whose snapshot does not depend on the period (the API keeps ONE per
// store): fetched once, not again on every period switch.
const PERIODLESS = { executive: { wa: "month" } };

/** One query per part, so a slow part never blanks the page. Key: store + period + part. */
const useParts = (view, parts, period, storeKey, enabled) => {
  const qc = useQueryClient();
  const userId = useCacheOwner();
  const results = useQueries({
    queries: parts.map((part) => {
      const p = PERIODLESS[view]?.[part] || period;
      return snapshotQuery({ key: dashKey(view, storeKey, p, part), path: view, params: { period: p, part }, enabled, userId, qc });
    }),
  });
  return Object.fromEntries(parts.map((p, i) => [p, results[i]]));
};

export const useDashboardStore = (period, storeKey, enabled = true) => useParts("store", STORE_PARTS, period, storeKey, enabled);

export const useDashboardExecutive = (period, storeKey, enabled = true) => useParts("executive", EXECUTIVE_PARTS, period, storeKey, enabled);

export const useDashboardReports = (period, offset, storeKey, enabled = true) => {
  const qc = useQueryClient();
  const userId = useCacheOwner();
  return useQuery(snapshotQuery({ key: dashKey("reports", storeKey, period, offset), path: "reports", params: { period, offset, async: 1 }, enabled, userId, qc }));
};

// ── Dashboard settings (CAC) — store owner / master ──────────────────────────
export const useDashboardSettings = (storeKey, enabled = true) => useListQuery({ name: "settings", storeKey, path: "settings", enabled });
